import './style.css';
import { AppView } from './types';
import { renderLandingPage } from './pages/landing';
import { renderPollPage } from './pages/poll';
import { renderSuccessPage } from './pages/success';

const app = document.querySelector<HTMLDivElement>('#app')!;

let currentView: AppView = AppView.LANDING;
let signupCustomId = '';

interface NotificationData {
  title: string;
  message: string;
  due: number;
  disable_login: boolean;
  active: boolean;
}

let cachedNotification: NotificationData | null = null;
let notificationFetched = false;

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  })[character] ?? character);
}

function isStaging(): boolean {
  const host = window.location.hostname;
  return host.includes('staging');
}

async function fetchNotification(): Promise<void> {
  if (notificationFetched || isStaging()) return;
  notificationFetched = true;
  try {
    const res = await fetch('/api/notifications');
    const json = await res.json();
    if (json.success && json.data) {
      cachedNotification = json.data as NotificationData;
      renderNotificationBanner();
    }
  } catch {
    /* non-critical */
  }
}

function renderNotificationBanner(): void {
  const existing = document.getElementById('oh-notification-banner');
  if (existing) existing.remove();
  if (!cachedNotification) return;

  const isWarning = cachedNotification.disable_login;
  const banner = document.createElement('div');
  banner.id = 'oh-notification-banner';
  banner.className = 'fixed top-0 left-0 right-0 z-[100] animate-slide-up';
  banner.style.cssText = 'opacity:0;transform:translateY(-10px);transition:all 0.4s cubic-bezier(0.16,1,0.3,1)';
  banner.innerHTML = `
    <div class="mx-auto max-w-4xl px-3 py-2 sm:px-4 sm:py-3">
      <div class="rounded-xl border shadow-lg backdrop-blur-sm
        ${isWarning
          ? 'bg-red-50/95 border-red-200/80 shadow-red-500/10'
          : 'bg-amber-50/95 border-amber-200/80 shadow-amber-500/10'
        }">
        <div class="flex items-start gap-3 px-4 py-3">
          <div class="flex-shrink-0 mt-0.5 rounded-full p-1.5
            ${isWarning
              ? 'bg-red-100 text-red-600'
              : 'bg-amber-100 text-amber-600'
            }">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              ${isWarning
                ? '<circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/>'
                : '<path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"/>'
              }
            </svg>
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold ${isWarning ? 'text-red-800' : 'text-amber-800'}">
              ${escapeHtml(cachedNotification.title)}
            </p>
            <p class="text-[13px] ${isWarning ? 'text-red-700' : 'text-amber-700'} mt-1 leading-relaxed whitespace-pre-line">
              ${escapeHtml(cachedNotification.message)}
            </p>
          </div>
          ${!isWarning ? `
            <button id="dismiss-notification" class="flex-shrink-0 p-1 rounded-lg text-amber-500 hover:text-amber-700 hover:bg-amber-100 transition-colors" aria-label="Close">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          ` : ''}
        </div>
      </div>
    </div>
  `;

  document.body.prepend(banner);
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      banner.style.opacity = '1';
      banner.style.transform = 'translateY(0)';
    });
  });

  document.getElementById('dismiss-notification')?.addEventListener('click', () => {
    banner.style.opacity = '0';
    banner.style.transform = 'translateY(-100%)';
    setTimeout(() => banner.remove(), 400);
  });

  if (isWarning) {
    disableSignupForm();
  }
}

function disableSignupForm(): void {
  const form = document.getElementById('signup-form') as HTMLFormElement | null;
  if (!form) return;
  form.querySelectorAll('input, button, select, textarea').forEach((el) => {
    (el as HTMLInputElement | HTMLButtonElement).disabled = true;
  });
  form.style.opacity = '0.5';
  form.style.pointerEvents = 'none';
}

function render(): void {
  app.innerHTML = '';

  switch (currentView) {
    case AppView.LANDING:
      app.appendChild(renderLandingPage((customId) => {
        signupCustomId = customId || '';
        currentView = customId ? AppView.POLL : AppView.SUCCESS;
        render();
      }));
      break;

    case AppView.POLL:
      app.appendChild(renderLandingPage(() => {}));
      app.appendChild(renderPollPage(signupCustomId, () => {
        currentView = AppView.SUCCESS;
        render();
      }));
      break;

    case AppView.SUCCESS:
      app.appendChild(renderSuccessPage());
      break;
  }

  if (cachedNotification?.disable_login) {
    setTimeout(disableSignupForm, 0);
  }
}

render();
fetchNotification();
